import { useState, useEffect } from 'react';
import { Star, Check } from 'lucide-react';
import { useSelector } from 'react-redux';

/**
 * Self-contained form for submitting/updating a rating
 */
export default function RatingForm({ ratings, submitRating, reload }) {
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState('');
  const [met, setMet] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const authUserId = useSelector((s) => s.user.authUserId);

  const myRating = ratings.find((r) => r.rater_id === authUserId);

  // Pre-fill if my review exists
  useEffect(() => {
    if (myRating) {
      setStars(myRating.stars);
      setComment(myRating.comment ?? '');
      setMet(myRating.met_in_person);
    }
  }, [myRating]);

  const handleSubmit = async () => {
    if (stars < 1) return;
    setSubmitting(true);
    const success = await submitRating({ stars, comment, met_in_person: met ?? false });
    setSubmitting(false);
    if (success) {
      await reload();
    }
  };

  function StarPicker({ value, onChange }) {
    return (
      <div className="flex items-center justify-between gap-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onClick={() => onChange(n)} className="flex-1">
            <Star
              className={`h-10 w-full ${n <= value ? 'fill-primary text-primary' : 'text-muted-foreground/40'}`}
            />
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border p-4">
      <h4 className="text-sm font-semibold">
        {myRating ? 'Update your review' : 'Write a review'}
      </h4>
      <div className="mt-2">
        <StarPicker value={stars} onChange={setStars} />
      </div>
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Optional — share your experience"
        className="mt-3 min-h-20 w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
      />
      <div className="mt-3">
        <div className="text-xs font-medium text-muted-foreground">
          Did you meet with this person?
        </div>
        <div className="mt-1 flex gap-2">
          <button
            type="button"
            onClick={() => setMet(true)}
            className={`flex-1 rounded-full border px-3 py-1.5 text-sm ${met === true ? 'border-primary bg-primary/10 text-primary' : 'border-border'}`}
          >
            Yes
          </button>
          <button
            type="button"
            onClick={() => setMet(false)}
            className={`flex-1 rounded-full border px-3 py-1.5 text-sm ${met === false ? 'border-primary bg-primary/10 text-primary' : 'border-border'}`}
          >
            No
          </button>
        </div>
      </div>
      <button
        disabled={stars < 1 || submitting}
        onClick={handleSubmit}
        className="mt-3 flex w-full items-center justify-center gap-1 rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50"
      >
        {submitting ? 'Submitting…' : myRating ? (<><Check className="h-4 w-4" />Update review</>) : 'Submit review'}
      </button>
    </div>
  );
}
